import type {
    AllWeeklyReportResult as AllWeeklyReportResultDto,
    DailyClientReportResult,
    DailyReportResult,
    LiceReportLocations as LiceReportLocationsDto,
    RangeClientReportResult,
    RangeReportResult,
} from '@/services/types';

import type {
    AllWeeklyReportResult,
    LiceReportLocations,
    LocationDailyLiceReport,
    LocationRangeLiceReport,
    RegionDailyLiceReport,
    RegionRangeLiceReport,
} from './types';

function mapLiceReportLocation(location: LiceReportLocationsDto): LiceReportLocations {
    return {
        ...location,
        cages: location.cages.map((cage) => ({
            ...cage,
            femaleTotal: cage.femaleTotal ?? null,
            femaleRawTotal: cage.femaleRawTotal ?? null,
            femaleReportingTotal: cage.femaleReportingTotal ?? null,
            caligusElongatusTotal: cage.caligusElongatusTotal ?? null,
            caligusElongatusRawTotal: cage.caligusElongatusRawTotal ?? null,
            caligusElongatusReportingTotal: cage.caligusElongatusReportingTotal ?? null,
            movingStageTotal: cage.movingStageTotal ?? null,
            movingStageRawTotal: cage.movingStageRawTotal ?? null,
            movingStageReportingTotal: cage.movingStageReportingTotal ?? null,
        })),
    };
}

/**
 * Every stored week, plus the interim one, with each pen's totals normalised.
 *
 * The weekly report is the only one of the four that never had a mapper: both containers called
 * the generated hook directly and read `data.data`. So `femaleRawTotal` and its siblings arrived
 * as `undefined` on weekly and `null` on daily and range, and each consumer absorbed the
 * difference on its own.
 */
export function mapAllWeeklyReportsToDomain(
    reports: AllWeeklyReportResultDto[]
): AllWeeklyReportResult[] {
    return reports.map((report) => ({
        ...report,
        locations: report.locations.map(mapLiceReportLocation),
    }));
}

export function mapDailyReportResultToDomain(
    dto: DailyReportResult | null | undefined
): LocationDailyLiceReport | null {
    if (!dto) return null;
    return {
        day: dto.day,
        location: mapLiceReportLocation(dto.location),
    };
}

export function mapDailyClientReportResultToDomain(
    dto: DailyClientReportResult | null | undefined
): RegionDailyLiceReport | null {
    if (!dto) return null;
    return {
        day: dto.day,
        locations: dto.locations.map((location) => mapLiceReportLocation(location)),
    };
}

export function mapRangeReportResultToDomain(
    dto: RangeReportResult | null | undefined
): LocationRangeLiceReport | null {
    if (!dto) return null;
    return {
        from: dto.from,
        to: dto.to,
        location: mapLiceReportLocation(dto.location),
    };
}

export function mapRangeClientReportResultToDomain(
    dto: RangeClientReportResult | null | undefined
): RegionRangeLiceReport | null {
    if (!dto) return null;
    return {
        from: dto.from,
        to: dto.to,
        locations: dto.locations.map((location) => mapLiceReportLocation(location)),
    };
}
